/**
 * 工程表の本体（#100、docs/39 §3.1〜§3.3、Figma A・A2）。左に表（つまみ・工程名と担当・開始・日数・進み・⋮）、右にバー。
 *   左の表は固定（横に流しても残る）、右は横にすべる。見出しは上に固定。ライブラリは使わず Pointer Events で作る（docs/39 決めごと9）
 * マウスのドラッグ（離したときに確定・Esc で元の位置）と、同じことをドラッグせずにできる方法（決めごと3＝WCAG 2.2 SC 2.5.7）：
 *   バーを動かす … バーを掴んで左右（日にちに吸いつく）／開始の欄（カレンダー）／バーを選んで ← →（1稼働日）
 *   伸ばす・縮める … バーの両端のつまみ／日数の欄／Shift＋← →
 *   前後をつなぐ … バーの右端の点を次のバーへ（引いている間は点線）／⋮ →「前の工程を選ぶ…」
 *   つなぎを外す … 線を押して選ぶ → Delete か ×／⋮ →「前の工程を選ぶ…」
 *   行を並べ替える … 左のつまみを上下（入る場所に藍の線。見出しは中の行ごと）／⋮ →「上へ」「下へ」／Alt＋↑↓
 * 計算（終わり・押し出し・輪）は packages/contract の関数だけ。ここは描くのと、動かした結果を親に渡すだけ
 * #101：金額の列（社内用・印刷に出さない）、中止の日（見出しに ☂／止、押すと「中止を取り消す」）、遅れのしるし（バーの右に「n日遅れ」・進みを朱の太字）、
 *   「休日も」の印は担当の行（2行目）の右、表の幅は表とバーの境をドラッグ（← → でも）
 * #105（Figma A3）：行の高さはどれも 44。中止は 現場全体＝見出しの ☂ と列ぜんぶに地色／工程を選んだ日＝☂ の横に止めた工程の数、
 *   地色は止めた工程の行のその日だけ（バーの切れ目に小さな ☂）。「雨でも」の印は2行目。凡例は左の表の見出しの「？」
 * #102（docs/39 §5.2・§5.3.5）：人数の列（「人」44px・見出しの行は中の工程の合計。#297 から既定は出さない）、重なりの印（2行目の朱の「重なり」→ 吹き出し）。
 *   2行目の「・n人」は #297 でやめた
 *   タッチ（指で触ったとき）：バーは長押し（0.4秒）でつかむ。つかむまでは1本指で表がスクロールする。選んだバーの両端に丸いつまみ（押せる所 44px）。
 *   つなぐのは ⋮「前の工程を選ぶ…」だけ（指のドラッグではつながない）。1日の幅（px）は親が決める（2本指で拡大・縮小）
 * #290（docs/102 §2.1・#291 §2.1.1）：旬・月の表示は紙の旬・月と同じ描き方＝日曜・祝日を塗らず、バーを休みで区切らず1本、会社の休みと中止は帯と名前
 */
import {
  addDays,
  addWorkDays,
  barDonePct,
  blockOf,
  headingDoneText,
  nextTaskState,
  stateMarkOf,
  taskStateOf,
  TASK_STATE_LABEL,
  calendarForRow,
  dateRuns,
  dayNo,
  headingAmountOf,
  headingSummary,
  isOffDay,
  calendarReleasing,
  isBaseOffDay,
  isSiteWorkDay,
  isStopDay,
  lanesOf,
  mdOf,
  mdwJaOf,
  rowDayState,
  dayNotesByRow,
  spanText,
  moveBlockTo,
  normalizeSchedule,
  visibleRows,
  weekdayOf,
  wouldCreateCycle,
  WEEKDAY_JA,
  type CompanyOffDay,
  type ScheduleAssignee,
  type ScheduleAssignees,
  type ScheduleConflict,
  type ScheduleDoc,
  type ScheduleRow,
  type ScheduleStop,
  type ScheduleDayNote,
  type WorkCalendar,
} from "@genbaryoku/contract";
import { PARTNER_LINK_MSG, SCHEDULE_EXPORT_MSG, SCHEDULE_MSG, showsRain, stopOtherText } from "./msg.js";
import { t } from "../../labels.js";
import { Fragment, createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent as RKeyboardEvent, type PointerEvent as RPointerEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { yen } from "../../lib/num.js";
import { CalendarPop } from "../../components/CalendarPop.jsx";
import { PointMenu, RowMenu, type RowMenuItem } from "../../components/RowMenu.jsx";
import { DayNoteMarks, type DayNoteTarget } from "./DayNotes.jsx";
import { toHalf } from "../../lib/num.js";
import { AssigneeInput } from "./AssigneeInput.jsx";
import { ALL_COLS, PX, ROW_H, colsTemplate, daysOf, dragPatch, scaleOf, textWidth, workRuns, type TableCols, type TimeScale, type View } from "./timeline.js";
import { JunTicks, ScaleLines } from "./scale.jsx";

/** 日付 → 位置（#291 §2.1.1：旬は1日の幅が旬の日数で変わる）。x＝その日の左端・xr＝右端・w＝その日の幅 */
export type Axis = { x: (ymd: string) => number; xr: (ymd: string) => number; w: (ymd: string) => number };
/** 「10/13」の幅（10px の字）と、バーの両端の日付の隙間 */
const BDATE_GAP = 4;
const bdateW = (t: string) => Math.ceil(textWidth(t, 10)) + 2;

export type Save = { save: "now" | "debounce"; /** 元に戻すの控えのまとまり（同じ key の続けての変更は1回ぶん） */ key?: string };
export type GanttOps = {
  /** 1行を直す（終わり・押し出しは親が normalizeSchedule で） */
  update: (id: string, patch: Partial<ScheduleRow>, how: Save) => void;
  /** 工程表まるごと（ドラッグ・並べ替え・つなぎ） */
  replace: (next: ScheduleDoc, how: Save) => void;
  remove: (index: number) => void;
  menuItems: (index: number) => RowMenuItem[];
  registerVendor: (name: string) => Promise<string | null>;
  /** 新しい工程・節目の既定の日 */
  defaultStart: (index: number) => string;
  /** その行のバーが見える所まで横に流す（日付を選んだ・日数を変えたあと） */
  reveal: (id: string) => void;
  /** 中止を取り消す（見出しの ☂ から。#101） */
  stopClick: (date: string) => void;
  /** その{task}のその日を 休み ⇄ 作業（#293。選んだ行のバーの日をクリック） */
  dayToggle?: (rowId: string, date: string) => void;
  /** 日付の見出しのクリック：{siteShort}の休み ⇄ 解除（#293。中止の日は中止のポップ） */
  siteOff?: (date: string) => void;
  /** 日のメモの吹き出しを開く（#299。右クリックの「この日のメモ」・点線を押す。rowId null＝日付の見出しのメモ） */
  noteEdit?: (t: DayNoteTarget) => void;
  /** 中止の窓をその日で開く（#299。右クリックの「{task}中止」＝rowId・見出しの「中止」） */
  stopOpen?: (date: string, rowId?: string) => void;
  /** 表の幅（px。done＝離したとき・キーのとき＝覚える。#101） */
  leftChange: (px: number, done: boolean) => void;
  /** 表の幅を自動（中身に合わせる）に戻す（境のダブルクリック・⋮。#102 §5.1 の2） */
  leftReset: () => void;
  /** 重なりの印を押した（吹き出しを開く。#102） */
  conflict?: (rowId: string, anchor: HTMLElement) => void;
  /** 工程の「報告」を押した（その工程の協力会社からの報告の吹き出し。#103） */
  report?: (rowId: string, anchor: HTMLElement) => void;
  /** 工程の「写真 n」を押した（写真を大きく。#103） */
  photos?: (rowId: string) => void;
  /** 表の「状況」のセルを押した（#292。％ OFF の会社：未着手 → 着手 → 完了 → 未着手） */
  stateClick?: (rowId: string) => void;
};

/**
 * 会社の設定「進捗を％で入力」（#292、docs/102 §4）。false（既定）＝表の列は「状況」（未着手／着手／完了）・バーは 完了＝全部・着手＝半分。
 * 親（Schedule.tsx）が Provider で渡す。省けば％（前の形）
 */
export const PctContext = createContext(true);

/** 指の長押しから来た contextmenu か（Chrome は PointerEvent で pointerType が付く。付かないブラウザはマウスとみなす。#299） */
const isTouchMenu = (e: React.MouseEvent): boolean => {
  const t = (e.nativeEvent as PointerEvent).pointerType;
  return !!t && t !== "mouse";
};
/** 長押しでつかむまでの時間（タッチ。docs/39 §5.2 の11） */
const LONG_PRESS_MS = 400;
/** つかんでいる間は表をスクロールさせない（touchmove を止める。passive にしない） */
const stopTouchScroll = (e: TouchEvent) => {
  if (e.cancelable) e.preventDefault();
};

type Drag =
  /** day・sel（#293）：押した日と、押したときにその行を選んでいたか（動かさずに離したら その日を 休み ⇄ 作業） */
  | { t: "move" | "resl" | "resr"; id: string; x0: number; orig: ScheduleDoc; moved: boolean; day?: string | null; sel?: boolean }
  | { t: "connect"; id: string; x0: number; y0: number; over: string | null }
  | { t: "row"; id: string; y0: number; gap: number | null; moved: boolean }
  | { t: "width"; x0: number; w0: number; w?: number };

/** 行の種類の呼び名（工程は会社の言葉 {task}。#142 段5 No.92・94） */
const rowName = (r: ScheduleRow) => r.name || "（名前なし）";
const isField = (el: Element | null) => !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || (el as HTMLElement).isContentEditable);

export function Gantt({
  doc,
  cal,
  view,
  range,
  today,
  readOnly,
  cands,
  selectedId,
  onSelect,
  ops,
  scrollerRef,
  left,
  leftDay,
  showAmount = false,
  delays = new Map<string, number>(),
  stops = [],
  help,
  pxOverride,
  showCrew = false,
  conflicts = new Map<string, ScheduleConflict>(),
  marks,
  companyOffDays = [],
  summary = false,
  barDatesAll = false,
  cols = ALL_COLS,
  dayNotes = [],
}: {
  doc: ScheduleDoc;
  cal: WorkCalendar;
  view: View;
  range: { start: string; days: number };
  today: string;
  readOnly: boolean;
  cands: ScheduleAssignees | undefined;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  ops: GanttOps;
  scrollerRef: React.RefObject<HTMLDivElement | null>;
  /** 左の表の幅（px） */
  left: number;
  /** いま見えている最初の日（月の名前を出すか決める） */
  leftDay: string;
  /** 金額の列（社内用）を出す */
  showAmount?: boolean;
  /** 知らせる遅れ（行の id → 日数） */
  delays?: ReadonlyMap<string, number>;
  /** 中止の日（見出しの ☂／止） */
  stops?: readonly ScheduleStop[];
  /** 左の表の見出しの「？」（凡例と使い方） */
  help?: ReactNode;
  /** 1日の幅（px）。2本指で拡大・縮小したとき（#102）。無ければ表示の既定 */
  pxOverride?: number | undefined;
  /** 人数の列（#102） */
  showCrew?: boolean;
  /** 重なり（行の id → 重なり。#102） */
  conflicts?: ReadonlyMap<string, ScheduleConflict>;
  /** 協力会社からの報告の確かめ待ちがある工程・反映した写真の数（#103） */
  marks?: { reports: ReadonlySet<string>; photos: ReadonlyMap<string, number> };
  /** 会社の休み（名前つき。旬・月の表示の帯に名前を出す。#290） */
  companyOffDays?: readonly CompanyOffDay[];
  /** 概略（#291）：見出しの行だけ・中の{task}を名前つきで横に並べる（見るだけ） */
  summary?: boolean;
  /** バーの両端の日付を日の表示でも全部の行に（⋮「表示設定」のバーの日付「常に表示」。#291 §2.1.1・#298）。旬・月はいつも・日は選んだ行だけ */
  barDatesAll?: boolean;
  /** 表の列（⋮「表示設定」。#296）：隠した列は描かない（幅は工程・担当の列に回す＝親が left を決める） */
  cols?: TableCols;
  /** 日のメモ（#299）：行のセルの下辺の朱の点線（旬・月は点）・日付の見出しの点 */
  dayNotes?: readonly ScheduleDayNote[];
}) {
  const px = pxOverride ?? PX[view];
  const [preview, setPreview] = useState<ScheduleDoc | null>(null);
  /** 吹き出し：つないでいるときは text、バーをつかんだときは 2行（上段＝期間・下段＝日数。#291 §2.1.1） */
  const [tip, setTip] = useState<{ x: number; y: number; text?: string; from?: string; to?: string; days?: string } | null>(null);
  const [linkLine, setLinkLine] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
  const [gapY, setGapY] = useState<number | null>(null);
  const [selLink, setSelLink] = useState<string | null>(null);
  const [cal0, setCalFor] = useState<{ id: string; anchor: HTMLElement } | null>(null);
  const [stopPop, setStopPop] = useState<{ date: string; anchor: HTMLElement } | null>(null);
  const stopOf = useMemo(() => new Map(stops.map((x) => [x.date, x])), [stops]);
  /** 右クリック・長押しのメニュー（#299）：{task}の行の日（row）か日付の見出し（head） */
  const [dayMenu, setDayMenu] = useState<{ kind: "row" | "head"; rowId: string | null; date: string; at: { x: number; y: number }; anchor?: HTMLElement } | null>(null);
  const notesOf = useMemo(() => dayNotesByRow(dayNotes), [dayNotes]);
  const [vw, setVw] = useState(900);
  const drag = useRef<Drag | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const shown = preview ?? doc;
  const rows = shown.rows;
  const vis = useMemo(() => visibleRows(rows), [rows]);
  const fullIndex = useMemo(() => new Map(rows.map((r, i) => [r.id, i])), [rows]);
  const tops = useMemo(() => {
    const m = new Map<string, number>();
    let y = 0;
    for (const r of vis) {
      m.set(r.id, y);
      y += ROW_H[r.kind];
    }
    return { m, total: y };
  }, [vis]);
  const dayList = useMemo(() => daysOf(range.start, range.days), [range.start, range.days]);
  const sc = useMemo(() => scaleOf(view, range.start, range.days, px), [view, range.start, range.days, px]);
  const width = sc.total;
  const ix = (ymd: string) => dayNo(ymd) - dayNo(range.start);
  const x = (ymd: string) => sc.x(ix(ymd));
  const ax: Axis = { x, xr: (ymd) => sc.x(ix(ymd) + 1), w: (ymd) => sc.w(ix(ymd)) };
  /**
   * 旬・月の表示（#290、docs/102 §2.1・#291 §2.1.1。紙の旬・月と同じ描き方）：日曜・祝日の地色を塗らず、バーを休みで区切らず1本。
   * 会社の休み（この工程表が使うとき）と{siteShort}全体の中止の期間は帯と名前（1日の中止は細い線）
   */
  const coarse = view !== "day";
  const bands = useMemo(() => {
    if (!coarse) return [];
    const out: { kind: "off" | "stop"; from: string; to: string; name: string }[] = [];
    for (const o of companyOffDays) if (cal.companyOff?.has(o.from) || cal.companyOff?.has(o.to)) out.push({ kind: "off", from: o.from, to: o.to, name: o.name });
    for (const run of dateRuns(stops.filter((s) => s.scope === "all" && s.reason !== "off").map((s) => s.date))) {
      const st = stopOf.get(run.from);
      out.push({ kind: "stop", from: run.from, to: run.to, name: st?.reason === "rain" && showsRain() ? SCHEDULE_MSG.stopButton : stopOtherText(st?.note) });
    }
    // {siteShort}の休み（#293）：会社の休みと同じ帯「休み」（メモがあればメモ）。{task}の休みは描かない
    for (const run of dateRuns(stops.filter((s) => s.reason === "off").map((s) => s.date))) {
      out.push({ kind: "off", from: run.from, to: run.to, name: stopOf.get(run.from)?.note || SCHEDULE_EXPORT_MSG.siteOffBand });
    }
    return out;
  }, [coarse, companyOffDays, cal, stops, stopOf]);

  // 表とバーの幅いっぱいのメモ：見えている枠の幅（横に流しても残る）
  useLayoutEffect(() => {
    // 親の枠の ref はこの部品の layout effect より後に付くので、自分の要素からたどる
    const el = scrollerRef.current ?? (bodyRef.current?.closest(".sch-scroll") as HTMLDivElement | null);
    if (!el) return;
    const ro = new ResizeObserver(() => setVw(el.clientWidth));
    ro.observe(el);
    setVw(el.clientWidth);
    return () => ro.disconnect();
  }, [scrollerRef]);

  // 選んだつなぎは Delete で外す（欄の中で打っているときは除く）
  useEffect(() => {
    if (!selLink) return;
    const onKey = (e: KeyboardEvent) => {
      if (isField(document.activeElement)) return;
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        removeLink(selLink);
      } else if (e.key === "Escape") setSelLink(null);
    };
    const onDown = (e: MouseEvent) => {
      if (!(e.target as Element).closest?.("[data-link-ui]")) setSelLink(null);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  });

  const removeLink = (key: string) => {
    const [from, to] = key.split(">");
    ops.replace({ rows: doc.rows, links: doc.links.filter((l) => !(l.from === from && l.to === to)) }, { save: "now" });
    setSelLink(null);
  };

  /* ---------------- 右クリックのメニュー（#299、docs/102 §5.10） ---------------- */
  /** {task}の行の日のセルを右クリック（日の表示・直せるとき）：その行・その日のメニュー。左の表・見出し・節目・メモの行は出さない */
  const onBodyMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    // 指の長押しはバーをつかむ（#102）＝メニューを出さない（タブレット・スマホは{task}の ⋮「日ごとのメモ」）
    if (readOnly || summary || view !== "day" || isTouchMenu(e)) return;
    const el = e.target as HTMLElement;
    const rowEl = el.closest?.<HTMLElement>(".sch-row");
    if (!rowEl || rowEl.dataset.kind !== "task" || !el.closest(".sch-t") || el.closest(".sch-l")) return;
    const idx = sc.idxAt(bodyPoint(e).x);
    if (idx < 0 || idx >= range.days) return;
    e.preventDefault();
    setDayMenu({ kind: "row", rowId: rowEl.dataset.rowId!, date: addDays(range.start, idx), at: { x: e.clientX, y: e.clientY } });
  };
  const dayMenuItems = (m: NonNullable<typeof dayMenu>): RowMenuItem[] => {
    const note: RowMenuItem = {
      label: SCHEDULE_MSG.dayNoteItem,
      onSelect: () => {
        const r = m.rowId ? doc.rows.find((z) => z.id === m.rowId) : undefined;
        ops.noteEdit?.({ rowId: m.rowId, date: m.date, at: m.at, ...(r ? { name: r.name } : {}) });
      },
      testId: "sch-dm-note",
    };
    if (m.kind === "row") {
      const r = doc.rows.find((z) => z.id === m.rowId);
      if (!r || r.kind !== "task") return [note];
      const inRange = !!r.start && m.date >= r.start && m.date <= (r.end ?? r.start);
      const st = rowDayState(r, m.date, cal);
      // 休み・作業（#293 のバーの日のクリックと同じ）：その{task}の範囲の日だけ。中止・{siteShort}の休みの日は出さない
      const day: RowMenuItem[] =
        inRange && ops.dayToggle && st.why !== "stop" && st.why !== "siteOff"
          ? [
              st.base
                ? { label: SCHEDULE_MSG.dayMenuOff, checked: !st.work, onSelect: () => ops.dayToggle!(r.id, m.date), testId: "sch-dm-off" }
                : { label: SCHEDULE_MSG.dayMenuWork, checked: st.work, onSelect: () => ops.dayToggle!(r.id, m.date), testId: "sch-dm-work" },
            ]
          : [];
      const stop: RowMenuItem[] = ops.stopOpen ? [{ label: SCHEDULE_MSG.stopThisRow, onSelect: () => ops.stopOpen!(m.date, r.id), testId: "sch-dm-stop" }] : [];
      return [note, ...day, ...stop];
    }
    // 日付の見出し：{siteShort}の休み（休みの日なら{siteShort}の作業日）・中止（中止の日は ✓ で、押すと中止のポップ）
    const st0 = stopOf.get(m.date);
    const so = st0?.reason === "off";
    const stopped = !!st0 && !so;
    const site: RowMenuItem[] =
      stopped || !ops.siteOff
        ? []
        : !so && isBaseOffDay(m.date, cal)
          ? [{ label: SCHEDULE_MSG.dayMenuSiteWork, checked: isSiteWorkDay(m.date, cal), onSelect: () => ops.siteOff!(m.date), testId: "sch-dm-sitework" }]
          : [{ label: SCHEDULE_MSG.dayMenuSiteOff, checked: so, onSelect: () => ops.siteOff!(m.date), testId: "sch-dm-siteoff" }];
    const stop: RowMenuItem[] = [
      {
        label: SCHEDULE_MSG.dayMenuStop,
        checked: stopped,
        onSelect: () => (stopped ? m.anchor && setStopPop({ date: m.date, anchor: m.anchor }) : ops.stopOpen?.(m.date)),
        testId: "sch-dm-stop",
      },
    ];
    return [note, ...site, ...stop];
  };

  /* ---------------- ドラッグ ---------------- */
  const bodyPoint = (e: { clientX: number; clientY: number }) => {
    const r = bodyRef.current!.getBoundingClientRect();
    return { x: e.clientX - r.left - left, y: e.clientY - r.top };
  };
  const endDrag = (commit: boolean) => {
    const d = drag.current;
    drag.current = null;
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
    window.removeEventListener("keydown", onEsc, true);
    window.removeEventListener("touchmove", stopTouchScroll);
    document.body.classList.remove("sch-dragging");
    setTip(null);
    setLinkLine(null);
    setGapY(null);
    if (!d || !commit) {
      setPreview(null);
      return;
    }
    if (d.t === "width") {
      // 押しただけ（動かしていない）なら覚えない（ダブルクリックで自動に戻すときに「手で変えた」にしない）
      if (d.w != null) ops.leftChange(d.w, true);
    } else if (d.t === "move" || d.t === "resl" || d.t === "resr") {
      const p = previewRef.current;
      setPreview(null);
      if (d.moved && p) ops.replace(p, { save: "now" });
      // 選んでいた行のバーの日を、動かさずに押して離した（#293）：その日を 休み ⇄ 作業
      else if (d.t === "move" && d.sel && d.day && ops.dayToggle) ops.dayToggle(d.id, d.day);
      else onSelect(d.id);
    } else if (d.t === "connect") {
      if (d.over && d.over !== d.id && !wouldCreateCycle(doc.links, d.id, d.over) && !doc.links.some((l) => l.from === d.id && l.to === d.over)) {
        ops.replace({ rows: doc.rows, links: [...doc.links, { from: d.id, to: d.over }] }, { save: "now" });
      }
    } else if (d.t === "row" && d.moved && d.gap != null) {
      const i = fullIndex.get(d.id)!;
      const gapFull = d.gap >= vis.length ? rows.length : fullIndex.get(vis[d.gap]!.id)!;
      let next = moveBlockTo(doc.rows, i, gapFull);
      // たたんだ見出しの中に入れたら、見出しをひらく（入れた行が隠れないように）
      const at = next.findIndex((r) => r.id === d.id);
      if (next[at]!.kind !== "heading") {
        for (let k = at - 1; k >= 0; k--) {
          if (next[k]!.kind !== "heading") continue;
          if (next[k]!.collapsed) next = next.map((r, j) => (j === k ? { ...r, collapsed: false } : r));
          break;
        }
      }
      ops.replace({ rows: next, links: doc.links }, { save: "now" });
    }
  };
  const previewRef = useRef<ScheduleDoc | null>(null);
  const onEsc = (e: KeyboardEvent) => {
    if (e.key !== "Escape" || !drag.current) return;
    e.preventDefault();
    e.stopPropagation();
    endDrag(false);
  };
  const onMove = (e: PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    if (d.t === "width") {
      d.w = d.w0 + (e.clientX - d.x0);
      ops.leftChange(d.w, false);
      return;
    }
    if (d.t === "move" || d.t === "resl" || d.t === "resr") {
      const r = d.orig.rows.find((z) => z.id === d.id)!;
      // 旬（#291 §2.1.1）は1日の幅が旬ごとに違う＝動かした端の位置がどの日に入るかで数える
      const dxp = e.clientX - d.x0;
      const s0 = r.start ?? range.start;
      const e0 = r.end ?? s0;
      const dd =
        view !== "jun"
          ? Math.round(dxp / px)
          : d.t === "resr"
            ? sc.idxAt(ax.xr(e0) + dxp - ax.w(e0) / 2) - ix(e0)
            : sc.idxAt(x(s0) + dxp + ax.w(s0) / 2) - ix(s0);
      // 「休みの日も作業する」工程は休みの無い暦（dragPatch の中で calendarForRow）
      const patch = dragPatch(r, d.t, dd, cal);
      if (!patch || !r.start) return;
      if (dd !== 0) d.moved = true;
      // 休みの指定のある日に開始を置いたら、離したあとと同じくその指定を外して見せる（#296）
      const next = normalizeSchedule({ rows: d.orig.rows.map((z) => (z.id === d.id ? { ...z, ...patch } : z)), links: d.orig.links }, calendarReleasing(cal, r, patch.start));
      previewRef.current = next;
      setPreview(next);
      const nr = next.rows.find((z) => z.id === d.id)!;
      // 2行の吹き出し（#291 §2.1.1）：上段「10/13（火）〜 11/14（土）」・下段「28日」（伸縮なら「28日 → 30日」）。節目は日付だけ
      if (r.kind === "milestone") setTip({ x: e.clientX, y: e.clientY, from: nr.start! });
      else setTip({ x: e.clientX, y: e.clientY, from: nr.start!, to: nr.end ?? nr.start!, days: d.t === "move" ? `${nr.days ?? 1}日` : `${r.days ?? 1}日 → ${nr.days ?? 1}日` });
    } else if (d.t === "connect") {
      const p = bodyPoint(e);
      const under = document.elementsFromPoint(e.clientX, e.clientY).map((el) => (el as HTMLElement).closest?.("[data-bar-id]") as HTMLElement | null).find(Boolean);
      const over = under?.dataset.barId ?? null;
      d.over = over && over !== d.id ? over : null;
      const from = rows.find((z) => z.id === d.id)!;
      const pt = barEnd(from);
      if (pt) setLinkLine({ x1: pt.x, y1: pt.y, x2: p.x, y2: p.y });
      const to = d.over ? rows.find((z) => z.id === d.over) : undefined;
      setTip(to ? { x: e.clientX, y: e.clientY, text: wouldCreateCycle(doc.links, d.id, to.id) ? "循環するためつなげません" : `${rowName(from)} → ${rowName(to)}` } : null);
    } else if (d.t === "row") {
      const p = bodyPoint(e);
      if (Math.abs(e.clientY - d.y0) > 4) d.moved = true;
      let gap = vis.length;
      for (let k = 0; k < vis.length; k++) {
        const t = tops.m.get(vis[k]!.id)!;
        if (p.y < t + ROW_H[vis[k]!.kind] / 2) {
          gap = k;
          break;
        }
      }
      d.gap = gap;
      setGapY(gap >= vis.length ? tops.total : tops.m.get(vis[gap]!.id)!);
    }
  };
  const onUp = () => endDrag(true);
  const begin = (dr: Drag) => {
    drag.current = dr;
    previewRef.current = null;
    setSelLink(null);
    document.body.classList.add("sch-dragging");
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    window.addEventListener("keydown", onEsc, true);
    window.addEventListener("touchmove", stopTouchScroll, { passive: false });
  };
  const pressTimer = useRef<{ cancel: () => void } | null>(null);
  const start = (e: RPointerEvent, dr: Drag) => {
    if (readOnly || e.button !== 0) return;
    // 指で触ったとき（#102）：バーを動かすのは長押し（0.4秒）でつかんでから。それまでに指が動いたらスクロールに任せる。
    //   つなぐのは ⋮ から（指のドラッグではつながない）。両端のつまみ・並べ替えのつまみは すぐつかむ（はっきりした持ち手）
    if (e.pointerType === "touch" && dr.t === "connect") return;
    if (e.pointerType === "touch" && dr.t === "move") {
      e.stopPropagation();
      pressTimer.current?.cancel();
      const x0 = e.clientX;
      const y0 = e.clientY;
      const pid = e.pointerId;
      let last = { x: x0, y: y0 };
      const cleanup = () => {
        clearTimeout(timer);
        window.removeEventListener("pointermove", watch);
        window.removeEventListener("pointerup", tap);
        window.removeEventListener("pointercancel", cleanup);
        pressTimer.current = null;
      };
      // 長押しの前に指を離した：選んでいた行のバーの日なら その日を 休み ⇄ 作業（#293。マウスのクリックと同じ）
      const tap = (ev: PointerEvent) => {
        cleanup();
        if (ev.pointerId === pid && dr.t === "move" && dr.sel && dr.day && ops.dayToggle) ops.dayToggle(dr.id, dr.day);
      };
      const watch = (ev: PointerEvent) => {
        if (ev.pointerId !== pid) return;
        last = { x: ev.clientX, y: ev.clientY };
        if (Math.hypot(ev.clientX - x0, ev.clientY - y0) > 8) cleanup();
      };
      const timer = setTimeout(() => {
        cleanup();
        onSelect(dr.id);
        navigator.vibrate?.(10);
        begin({ ...dr, x0: last.x } as Drag);
      }, LONG_PRESS_MS);
      window.addEventListener("pointermove", watch);
      window.addEventListener("pointerup", tap);
      window.addEventListener("pointercancel", cleanup);
      pressTimer.current = { cancel: cleanup };
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    begin(dr);
  };
  useEffect(
    () => () => {
      pressTimer.current?.cancel();
      endDrag(false);
    },
    [],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  /** バーの右端（つなぎの始まり）。節目は ◆ の右 */
  function barEnd(r: ScheduleRow): { x: number; y: number } | null {
    const top = tops.m.get(r.id);
    if (top == null || !r.start) return null;
    const y = top + ROW_H[r.kind] / 2;
    if (r.kind === "milestone") return { x: x(r.start) + ax.w(r.start) / 2 + 8, y };
    return r.end ? { x: ax.xr(r.end), y } : null;
  }
  function barStart(r: ScheduleRow): { x: number; y: number } | null {
    const top = tops.m.get(r.id);
    if (top == null || !r.start) return null;
    const y = top + ROW_H[r.kind] / 2;
    return { x: r.kind === "milestone" ? x(r.start) + ax.w(r.start) / 2 - 8 : x(r.start), y };
  }

  /* ---------------- キー（バーを選んで） ---------------- */
  const onBarKey = (e: RKeyboardEvent, r: ScheduleRow) => {
    if (readOnly || !r.start) return;
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const dir = e.key === "ArrowRight" ? 1 : -1;
    if (e.shiftKey && r.kind === "task") {
      ops.update(r.id, { days: Math.max(1, Math.min(999, (r.days ?? 1) + dir)) }, { save: "debounce", key: `days:${r.id}` });
    } else if (!e.shiftKey) {
      const s = r.kind === "task" ? addWorkDays(r.start, dir, calendarForRow(r, cal)) : addDays(r.start, dir);
      ops.update(r.id, r.kind === "task" ? { start: s } : { start: s, end: s }, { save: "debounce", key: `start:${r.id}` });
    }
  };

  /* ---------------- 描く ---------------- */
  const today0 = dayNo(today) >= dayNo(range.start) && dayNo(today) < dayNo(range.start) + range.days;
  // 見出しの目盛（日の表示を 20px 未満に縮めたときは旬の目盛）。縦線も同じ目盛で（#291 §2.1.1）
  const hview: View = view === "day" && px < 20 ? "jun" : view;

  return (
    <div
      className={"sch-inner" + (showAmount ? " amt" : "") + (showCrew ? " crew" : "") + (cols.start ? "" : " no-start") + (cols.days ? "" : " no-days") + (cols.state ? "" : " no-state")}
      style={{ width: left + width, ["--left" as string]: `${left}px`, ["--vw" as string]: `${vw}px`, ["--sch-cols" as string]: colsTemplate(cols, showCrew, showAmount) }}
    >
      <Header
        view={hview}
        days={dayList}
        sc={sc}
        cal={cal}
        today={today}
        left={left}
        leftIdx={Math.max(0, dayNo(leftDay) - dayNo(range.start))}
        showAmount={showAmount}
        showCrew={showCrew}
        stopOf={stopOf}
        readOnly={readOnly}
        help={help}
        onStop={(date, anchor) => setStopPop({ date, anchor })}
        onSiteOff={!readOnly && !summary && view === "day" && ops.siteOff ? ops.siteOff : undefined}
        onDayMenu={!readOnly && !summary && view === "day" ? (date, at, anchor) => setDayMenu({ kind: "head", rowId: null, date, at, anchor }) : undefined}
        headNotes={notesOf.get("")}
        onWidthDown={(e) => start(e, { t: "width", x0: e.clientX, w0: left })}
        onWidthKey={(dx) => ops.leftChange(left + dx, true)}
        onWidthReset={() => ops.leftReset()}
      />
      <div className={"sch-body" + (summary ? " sum" : "")} ref={bodyRef} style={{ height: summary ? summaryOf(rows, ax).total : tops.total }} onContextMenu={onBodyMenu}>
        {/* 休みの日の地色と今日の線（行の後ろ） */}
        <div className="sch-bg" aria-hidden="true" style={{ left, width }}>
          {coarse
            ? bands.map((b) =>
                b.kind === "stop" && b.from === b.to ? (
                  <span key={`s${b.from}`} className="sch-offband is-stop one" style={{ left: x(b.from) + ax.w(b.from) / 2 - 1 }} data-testid="sch-offband" data-kind="stop" title={b.name} />
                ) : (
                  <span key={`${b.kind}${b.from}`} className={"sch-offband" + (b.kind === "stop" ? " is-stop" : "")} style={{ left: x(b.from), width: ax.xr(b.to) - x(b.from) }} data-testid="sch-offband" data-kind={b.kind} title={b.name}>
                    {b.name ? <b>{b.name}</b> : null}
                  </span>
                ),
              )
            : px >= 4
            ? dayList.map((d, i) =>
                isStopDay(d, cal) ? (
                  <span key={d} className="sch-off is-stop" style={{ left: sc.x(i), width: sc.w(i) }} data-stop={d} />
                ) : isOffDay(d, cal) ? (
                  <span key={d} className="sch-off" style={{ left: sc.x(i), width: sc.w(i) }} />
                ) : null,
              )
            : null}
          {/* 縦線（#291 §2.1.1）：日＝日の境に点線／旬・月＝旬の境（11日・21日）に点線・月の境に実線。今日の線より下 */}
          {px >= 6 || hview !== "day" ? <ScaleLines view={hview} days={dayList} sc={sc} /> : null}
          {today0 ? <span className="sch-today" style={{ left: x(today) + ax.w(today) / 2 - 1 }} data-testid="sch-today" /> : null}
        </div>
        {summary ? <SummaryRows rows={rows} ax={ax} showCrew={showCrew} showAmount={showAmount} notes={notesOf} cal={cal} /> : null}
        {summary ? null : vis.map((r) => {
          const i = fullIndex.get(r.id)!;
          return (
            <Row
              key={r.id}
              r={r}
              i={i}
              rows={rows}
              sel={selectedId === r.id}
              readOnly={readOnly}
              cands={cands}
              ops={ops}
              onSelect={onSelect}
              onGrip={(e) => start(e, { t: "row", id: r.id, y0: e.clientY, gap: null, moved: false })}
              onOpenCal={(anchor) => setCalFor({ id: r.id, anchor })}
              showAmount={showAmount}
              showCrew={showCrew}
              conflict={conflicts.get(r.id)}
              delay={delays.get(r.id) ?? 0}
              reported={!!marks?.reports.has(r.id)}
              photoCount={marks?.photos.get(r.id) ?? 0}
              bar={
                <>
                {r.kind === "task" ? <RowStops r={r} cal={cal} ax={ax} px={px} stopOf={stopOf} /> : null}
                {r.kind === "task" ? <DayNoteMarks notes={notesOf.get(r.id)} ax={ax} cal={cal} coarse={coarse} name={r.name} rowId={r.id} readOnly={readOnly} onEdit={ops.noteEdit} /> : null}
                <Bar
                  r={r}
                  i={i}
                  rows={rows}
                  ax={ax}
                  cal={cal}
                  coarse={coarse}
                  dates={coarse || barDatesAll || selectedId === r.id}
                  readOnly={readOnly}
                  sel={selectedId === r.id}
                  dayHits={!readOnly && view === "day" && selectedId === r.id && !!ops.dayToggle}
                  onDown={(e, t) =>
                    start(
                      e,
                      t === "connect"
                        ? { t, id: r.id, x0: e.clientX, y0: e.clientY, over: null }
                        : {
                            t,
                            id: r.id,
                            x0: e.clientX,
                            orig: doc,
                            moved: false,
                            // 押した日（バーの日の目印 data-day。#293）と、押したときにこの行を選んでいたか
                            day: (e.target as HTMLElement).closest?.<HTMLElement>("[data-day]")?.dataset.day ?? null,
                            sel: selectedId === r.id,
                          },
                    )
                  }
                  onKey={(e) => onBarKey(e, r)}
                  onFocus={() => onSelect(r.id)}
                  delay={delays.get(r.id) ?? 0}
                />
                </>
              }
            />
          );
        })}
        {/* つなぎの線（概略では出さない） */}
        {summary ? null : (<>
        <svg className="sch-links" style={{ left, width, height: tops.total }} aria-hidden={readOnly ? true : undefined}>
          <defs>
            <marker id="sch-arrow" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M0,0 L8,4 L0,8 z" />
            </marker>
          </defs>
          {shown.links.map((l) => {
            const a = rows.find((z) => z.id === l.from);
            const b = rows.find((z) => z.id === l.to);
            const p1 = a ? barEnd(a) : null;
            const p2 = b ? barStart(b) : null;
            if (!p1 || !p2) return null;
            const key = `${l.from}>${l.to}`;
            const bt = tops.m.get(b!.id)!;
            const xm = p1.x + 6;
            const d =
              p2.x - 8 >= xm
                ? `M${p1.x},${p1.y} H${xm} V${p2.y} H${p2.x}`
                : `M${p1.x},${p1.y} H${xm} V${p2.y > p1.y ? bt : bt + ROW_H[b!.kind]} H${p2.x - 8} V${p2.y} H${p2.x}`;
            return (
              <g key={key} className={"sch-link" + (selLink === key ? " sel" : "")} data-link-ui>
                <path d={d} className="ln" markerEnd="url(#sch-arrow)" />
                {readOnly ? null : (
                  <path
                    d={d}
                    className="hit"
                    data-testid="sch-link"
                    data-link={key}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      setSelLink(key);
                    }}
                  />
                )}
              </g>
            );
          })}
          {linkLine ? <path className="sch-linking" d={`M${linkLine.x1},${linkLine.y1} L${linkLine.x2},${linkLine.y2}`} /> : null}
        </svg>
        {selLink && !readOnly
          ? (() => {
              const [from, to] = selLink.split(">");
              const b = rows.find((z) => z.id === to);
              const a = rows.find((z) => z.id === from);
              const p2 = b ? barStart(b) : null;
              if (!p2 || !a || !b) return null;
              return (
                <button type="button" className="sch-linkx" data-link-ui style={{ left: left + p2.x - 30, top: p2.y - 34 }} onClick={() => removeLink(selLink)} aria-label={`「${rowName(a)}」→「${rowName(b)}」のつなぎを解除`} data-testid="sch-link-x">
                  ×
                </button>
              );
            })()
          : null}
        </>)}
        {gapY != null ? <div className="sch-gap" style={{ top: gapY - 1 }} aria-hidden="true" data-testid="sch-gap" /> : null}
      </div>
      {tip ? (
        tip.from ? (
          <div className="sch-tip two" role="status" style={{ left: tip.x, top: tip.y - 62 }} data-testid="sch-tip">
            <span className="rng" data-testid="sch-tip-range">
              <TipDate d={tip.from} cal={cal} />
              {tip.to ? (
                <>
                  {"〜 "}
                  <TipDate d={tip.to} cal={cal} />
                </>
              ) : null}
            </span>
            {tip.days ? (
              <span className="sub" data-testid="sch-tip-days">
                {tip.days}
              </span>
            ) : null}
          </div>
        ) : (
          <div className="sch-tip" role="status" style={{ left: tip.x, top: tip.y - 44 }} data-testid="sch-tip">
            {tip.text}
          </div>
        )
      ) : null}
      {dayMenu ? (
        <PointMenu
          at={dayMenu.at}
          label={SCHEDULE_MSG.dayMenuLabel(mdwJaOf(dayMenu.date, cal.holidays))}
          items={dayMenuItems(dayMenu)}
          onClose={() => setDayMenu(null)}
          testId="sch-daymenu"
        />
      ) : null}
      {stopPop ? (
        <StopPop
          stop={stopOf.get(stopPop.date) ?? { date: stopPop.date, reason: "rain", note: null, scope: "all", rowIds: [] }}
          names={(stopOf.get(stopPop.date)?.rowIds ?? []).map((id) => rowName(rows.find((z) => z.id === id) ?? ({ kind: "task", name: "" } as ScheduleRow)))}
          anchor={stopPop.anchor}
          onClose={() => setStopPop(null)}
          onUndo={() => {
            setStopPop(null);
            ops.stopClick(stopPop.date);
          }}
        />
      ) : null}
      {cal0 ? (
        <CalendarPop
          value={rows.find((r) => r.id === cal0.id)?.start ?? null}
          anchor={cal0.anchor}
          label="開始日"
          onClose={() => setCalFor(null)}
          onPick={(v) => {
            const r = rows.find((z) => z.id === cal0.id);
            setCalFor(null);
            cal0.anchor.focus();
            if (!r) return;
            if (!v) ops.update(r.id, r.kind === "task" || r.kind === "heading" ? { start: null, days: null } : { start: null, end: null }, { save: "now" });
            // 見出しも 開始・日数を持てる（#291）
            else if (r.kind === "task" || r.kind === "heading") ops.update(r.id, { start: v, days: r.days ?? 1 }, { save: "now" });
            else ops.update(r.id, { start: v, end: v }, { save: "now" });
            if (v) ops.reveal(r.id);
          }}
        />
      ) : null}
    </div>
  );
}

/* ---------------- 見出し（月／日・曜日） ---------------- */
function Header({
  view,
  days,
  sc,
  cal,
  today,
  left,
  leftIdx,
  showAmount,
  showCrew,
  stopOf,
  readOnly,
  help,
  onStop,
  onSiteOff,
  onDayMenu,
  headNotes,
  onWidthDown,
  onWidthKey,
  onWidthReset,
}: {
  view: View;
  days: string[];
  sc: TimeScale;
  cal: WorkCalendar;
  today: string;
  left: number;
  leftIdx: number;
  showAmount: boolean;
  showCrew: boolean;
  stopOf: ReadonlyMap<string, ScheduleStop>;
  readOnly: boolean;
  help?: ReactNode;
  onStop: (date: string, anchor: HTMLElement) => void;
  /** 日付の見出しのクリック（#293）：{siteShort}の休み ⇄ 解除。中止の日は onStop（中止のポップ）。無ければ押せない */
  onSiteOff?: ((date: string) => void) | undefined;
  /** 日付の見出しの右クリック・長押し（#299）：小さなメニュー（この日のメモ・{siteShort}の休み／作業日・中止）。無ければ出さない */
  onDayMenu?: ((date: string, at: { x: number; y: number }, anchor: HTMLElement) => void) | undefined;
  /** 日付の見出しのメモ（#299。日付 → 文）：日の表示は日付の下に朱の点、旬・月は見出しの下に点 */
  headNotes?: ReadonlyMap<string, string> | undefined;
  onWidthDown: (e: RPointerEvent) => void;
  onWidthKey: (dx: number) => void;
  onWidthReset: () => void;
}) {
  const pct = useContext(PctContext);
  // 日付の見出しの長押し（#299。タッチ）：0.4秒でメニュー。開いたあとの click（{siteShort}の休みの切替）は飲み込む
  const press = useRef<{ timer: number; fired: boolean } | null>(null);
  const pressDown = (e: RPointerEvent<HTMLElement>, d: string) => {
    press.current = null;
    if (!onDayMenu || e.pointerType === "mouse") return;
    const el = e.currentTarget;
    const at = { x: e.clientX, y: e.clientY };
    const cur = { timer: 0, fired: false };
    cur.timer = window.setTimeout(() => {
      cur.fired = true;
      onDayMenu(d, at, el);
    }, LONG_PRESS_MS);
    press.current = cur;
  };
  const pressEnd = () => {
    if (press.current && !press.current.fired) window.clearTimeout(press.current.timer);
  };
  const pressSwallow = () => {
    const fired = !!press.current?.fired;
    press.current = null;
    return fired;
  };
  const months: { ym: string; from: number; n: number }[] = [];
  days.forEach((d, i) => {
    const ym = d.slice(0, 7);
    const last = months[months.length - 1];
    if (last && last.ym === ym) last.n++;
    else months.push({ ym, from: i, n: 1 });
  });
  return (
    <div className={"sch-head" + (view === "day" ? "" : " coarse") + (view === "month" ? " month" : "")}>
      <div className="sch-hl" style={{ width: left }}>
        <span className="c-grip" />
        <span className="c-name">
          {t("{task}・担当")}
          {help}
        </span>
        <span className="c-start">開始</span>
        <span className="c-days">日数</span>
        <span className="c-pg" data-testid="sch-pg-head">{pct ? "進捗" : SCHEDULE_MSG.stateColumn}</span>
        {showCrew ? (
          <span className="c-crew" data-testid="sch-crew-head" title={SCHEDULE_MSG.crewLabel}>
            {SCHEDULE_MSG.crewHead}
          </span>
        ) : null}
        {showAmount ? (
          <span className="c-amt sch-internal" data-testid="sch-amount-head">
            金額
            <small>{SCHEDULE_MSG.amountHead}</small>
          </span>
        ) : null}
        <span className="c-menu" />
        {readOnly ? null : (
          <span
            className="sch-resizer"
            role="separator"
            aria-orientation="vertical"
            aria-label="表の幅（← → で変更・ダブルクリックで自動幅）"
            aria-valuenow={left}
            title={SCHEDULE_MSG.resizerTitle}
            tabIndex={0}
            onPointerDown={onWidthDown}
            onDoubleClick={onWidthReset}
            onKeyDown={(e) => {
              if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
              e.preventDefault();
              onWidthKey(e.key === "ArrowRight" ? 16 : -16);
            }}
            data-testid="sch-resizer"
          >
            {/* 見えるつまみ（#296、docs/102 §2.4.1）：境の縦の線と、真ん中に点3つ（乗せる・つかむと主色） */}
            <i className="sch-grip-dots" aria-hidden="true" />
          </span>
        )}
      </div>
      <div className="sch-ht" style={{ width: sc.total }}>
        <div className="sch-months">
          {months.map((m, k) => (
            <span key={m.ym} style={{ left: sc.x(m.from), width: sc.x(m.from + m.n) - sc.x(m.from) }}>
              {/* 年は最初の月と1月だけ。名前は左に流れても見えている間は残る（sticky）。月の目盛は1段（#291 §2.1.1） */}
              {sc.x(m.from + m.n) - sc.x(Math.max(m.from, leftIdx)) >= (k === 0 || m.ym.endsWith("-01") ? 100 : 44) ? (
                <b>{k === 0 || m.ym.endsWith("-01") ? `${Number(m.ym.slice(0, 4))}年 ${Number(m.ym.slice(5))}月` : `${Number(m.ym.slice(5))}月`}</b>
              ) : null}
            </span>
          ))}
        </div>
        {view === "month" ? null : (
        <div className="sch-days">
          {view === "day"
            ? days.map((d, i) => {
                const hol = cal.holidays.has(d);
                // 曜日の色（#291 §2.1.1）：土＝青・日祝＝朱（日祝の地色は今のまま）
                const wd = weekdayOf(d);
                const st0 = stopOf.get(d);
                // {siteShort}の休み（#293）：見出しの字は「休」（墨）・地色は休みの日と同じ。中止の ☂・止 とは分ける
                const so = st0?.reason === "off";
                const st = so ? undefined : st0;
                // 工程を選んだ中止は ☂ の横に止めた工程の数（現場全体は数字なし。#105）
                const n = st?.scope === "rows" ? st.rowIds.length : 0;
                // ☂ は雨の印を出す製品だけ（営業力は協力会社の報告の中止も「止」。#142 段5）
                const rainy = st?.reason === "rain" && showsRain();
                // {siteShort}の作業日（#296）：休みの日でも全{task}が作業＝地色を外し、曜日の字は平日と同じ墨（朱・藍にしない）
                const sw = !st && !so && isSiteWorkDay(d, cal);
                // 押すと{siteShort}の作業日になる休みの日（暦の休み＝曜日・祝日・会社の休み）
                const baseOff = !st && !so && !sw && isBaseOffDay(d, cal);
                const mark = so ? SCHEDULE_MSG.siteOffMark : st ? `${rainy ? "☂" : "止"}${n ? n : ""}` : hol ? "祝" : WEEKDAY_JA[weekdayOf(d)];
                const label = st
                  ? `${mdOf(d)} ${rainy ? SCHEDULE_MSG.stopButton : stopOtherText(st.note)}${n ? t(`（${n}{task}）`) : t("（{siteShort}全体）")}`
                  : so
                    ? SCHEDULE_MSG.siteOffLabel(mdOf(d)) + (st0?.note ? `（${st0.note}）` : "")
                    : sw
                      ? SCHEDULE_MSG.siteWorkLabel(mdwJaOf(d, cal.holidays))
                      : undefined;
                const click = so ? SCHEDULE_MSG.siteOffUnclick : sw ? SCHEDULE_MSG.siteWorkUnclick : baseOff ? SCHEDULE_MSG.siteWorkClick : SCHEDULE_MSG.siteOffClick;
                // 日付の見出しのメモ（#299）：日付の下に朱の点。文は乗せたときの吹き出し（title）
                const note = headNotes?.get(d);
                return (
                  <span
                    key={d}
                    className={
                      "d" +
                      (isOffDay(d, cal) ? " off" : "") +
                      (sw ? " is-sitework" : hol || wd === 0 ? " sun" : wd === 6 ? " sat" : "") +
                      (st ? (n ? " is-stoprows" : " is-stop") : "") +
                      (so ? " so" : "") +
                      (d === today ? " today" : "")
                    }
                    style={{ left: sc.x(i), width: sc.w(i) }}
                    data-date={d}
                    title={!onSiteOff ? [sw ? label : "", note ? `${mdwJaOf(d, cal.holidays)} ${note}` : ""].filter(Boolean).join("\n") || undefined : undefined}
                    onContextMenu={
                      onDayMenu
                        ? (e) => {
                            e.preventDefault();
                            // 指の長押し（Android は contextmenu も来る）は上の長押しの控えが開く＝ここでは開かない
                            if (isTouchMenu(e)) return;
                            onDayMenu(d, { x: e.clientX, y: e.clientY }, e.currentTarget);
                          }
                        : undefined
                    }
                  >
                    {/* 日付の見出しのセルを押す（#293・#296）：平日 → {siteShort}の休み ⇄ 解除・休みの日 → {siteShort}の作業日 ⇄ 解除・中止の日は中止のポップ
                        （黙って上書きしない）。キーボードでは「中止」の窓の理由「{siteShort}の休み」・⋮「休日…」の節「作業日にした休日」で同じことができる
                        （Tab で56日を回らないよう tabIndex -1） */}
                    {onSiteOff ? (
                      <button
                        type="button"
                        className="dk"
                        tabIndex={-1}
                        title={[note ? `${mdwJaOf(d, cal.holidays)} ${note}` : "", st ? "" : sw ? `${label}\n${click}` : click].filter(Boolean).join("\n") || undefined}
                        aria-label={`${mdOf(d)} ${st ? SCHEDULE_MSG.stopUndo : click}`}
                        onPointerDown={(e) => pressDown(e, d)}
                        onPointerUp={pressEnd}
                        onPointerCancel={pressEnd}
                        onPointerLeave={pressEnd}
                        onClick={(e) => {
                          if (pressSwallow()) return;
                          if (st) onStop(d, e.currentTarget);
                          else onSiteOff(d);
                        }}
                        data-testid="sch-dayhead"
                        data-date={d}
                        data-sitework={sw ? "1" : undefined}
                      />
                    ) : null}
                    <b>{Number(d.slice(8))}</b>
                    {st && !readOnly ? (
                      <button type="button" className="stopmk" aria-label={`${label}（押すと解除）`} onClick={(e) => onStop(d, e.currentTarget)} data-testid="sch-stopmark">
                        {mark}
                      </button>
                    ) : (
                      <i aria-label={label}>{mark}</i>
                    )}
                    {note ? <span className="hnote" data-testid="sch-hnote" data-note-date={d} aria-hidden="true" /> : null}
                  </span>
                );
              })
            : <JunTicks days={days} sc={sc} />}
        </div>
        )}
        {/* 旬・月の表示の日付の見出しのメモ（#299）：見出しの下に朱の小さな点（位置は日） */}
        {view !== "day" && headNotes?.size ? (
          <div className="sch-hnotes" aria-hidden="true">
            {[...headNotes].map(([d, text]) => {
              const i = days.indexOf(d);
              if (i < 0) return null;
              return <span key={d} className="hnote" style={{ left: sc.x(i) + sc.w(i) / 2 - 3 }} title={`${mdwJaOf(d, cal.holidays)} ${text}`} data-testid="sch-hnote" data-note-date={d} />;
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** 吹き出しの日付「10/13（火）」：曜は 土＝青・日祝＝朱（黒地で読める明るさ。#291 §2.1.1） */
function TipDate({ d, cal }: { d: string; cal: WorkCalendar }) {
  const wd = weekdayOf(d);
  const cls = cal.holidays.has(d) || wd === 0 ? "sun" : wd === 6 ? "sat" : "";
  return (
    <>
      {mdOf(d)}（<span className={cls || undefined}>{WEEKDAY_JA[wd]}</span>）
    </>
  );
}

/**
 * バーの両端の日付（#291 §2.1.1）：始まりの日の左・終わりの日の右に「10/13」「11/14」（10px・薄墨）。right＝出さない右の位置（隣にぶつかるとき false）。
 * 1日だけのバーは左の1つだけ（同じ日を2つ並べない。11/3 本番の確かめ）
 */
const hasRightDate = (start: string, end: string): boolean => start !== end;
function BarDates({ start, end, l, r, top, showRight = true }: { start: string; end: string; l: number; r: number; top?: number; showRight?: boolean }) {
  const a = mdOf(start);
  const b = mdOf(end);
  return (
    <>
      <span className="sch-bdate l" style={{ left: l - BDATE_GAP - bdateW(a), width: bdateW(a), top }} data-testid="sch-bdate" aria-hidden="true">
        {a}
      </span>
      {showRight && hasRightDate(start, end) ? (
        <span className="sch-bdate r" style={{ left: r + BDATE_GAP, width: bdateW(b), top }} data-testid="sch-bdate" aria-hidden="true">
          {b}
        </span>
      ) : null}
    </>
  );
}

/* ---------------- 1行 ---------------- */
function Row({
  r,
  i,
  rows,
  sel,
  readOnly,
  cands,
  ops,
  onSelect,
  onGrip,
  onOpenCal,
  bar,
  showAmount,
  showCrew,
  conflict,
  delay,
  reported = false,
  photoCount = 0,
}: {
  r: ScheduleRow;
  i: number;
  rows: ScheduleRow[];
  sel: boolean;
  readOnly: boolean;
  cands: ScheduleAssignees | undefined;
  ops: GanttOps;
  onSelect: (id: string) => void;
  onGrip: (e: RPointerEvent) => void;
  onOpenCal: (anchor: HTMLElement) => void;
  bar: ReactNode;
  showAmount: boolean;
  showCrew: boolean;
  conflict: ScheduleConflict | undefined;
  delay: number;
  reported?: boolean;
  photoCount?: number;
}) {
  const pct = useContext(PctContext);
  const h = ROW_H[r.kind];
  // 人数の列（#102）：工程は数字の欄（1〜99。空は人数なし）、見出しは中の工程の合計
  const crew = showCrew ? (
    <span className="c-crew">
      {r.kind === "task" ? (
        <CrewInput r={r} readOnly={readOnly} ops={ops} />
      ) : r.kind === "heading" ? (
        (() => {
          const [a, b] = blockOf(rows, i);
          const sum = rows.slice(a + 1, b).reduce((n, x) => n + (x.kind === "task" ? (x.crew ?? 0) : 0), 0);
          return sum ? <span className="ro num" data-testid="sch-crew-sum">{sum}</span> : null;
        })()
      ) : null}
    </span>
  ) : null;
  const amt = showAmount ? (
    <span className="c-amt sch-internal" data-testid="sch-amount">
      {r.kind === "task" ? (r.amount != null ? `¥${yen(r.amount)}` : "—") : r.kind === "heading" ? (() => {
        const a = headingAmountOf(rows, i);
        return a != null ? `¥${yen(a)}` : "";
      })() : ""}
    </span>
  ) : null;
  const up = (patch: Partial<ScheduleRow>, key: string) => ops.update(r.id, patch, { save: "debounce", key: `${key}:${r.id}` });
  const pick = (e: React.MouseEvent) => {
    if (!(e.target as Element).closest("input,button,textarea,a")) onSelect(r.id);
  };
  const grip = readOnly ? (
    <span className="c-grip" />
  ) : (
    <span className="c-grip">
      <button type="button" className="sch-grip" onPointerDown={onGrip} aria-label={`「${rowName(r)}」の並べ替え（Alt＋↑↓でも）`} tabIndex={-1} data-testid="sch-grip">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="9" cy="6" r="1.6" />
          <circle cx="15" cy="6" r="1.6" />
          <circle cx="9" cy="12" r="1.6" />
          <circle cx="15" cy="12" r="1.6" />
          <circle cx="9" cy="18" r="1.6" />
          <circle cx="15" cy="18" r="1.6" />
        </svg>
      </button>
    </span>
  );
  const menu = readOnly ? <span className="c-menu" /> : <span className="c-menu"><RowMenu label={`「${rowName(r)}」のメニュー`} items={ops.menuItems(i)} testId="sch-menu" /></span>;
  const onRowKey = (e: RKeyboardEvent) => {
    if (readOnly) return;
    if (e.altKey && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
      e.preventDefault();
      const next = e.key === "ArrowUp" ? "up" : "down";
      const it = ops.menuItems(i).find((m) => m.testId === `sch-mi-${next}`);
      it?.onSelect?.();
    }
  };
  const common = {
    className: `sch-row k-${r.kind}${sel ? " sel" : ""}${r.kind === "task" && (r.progress ?? 0) >= 100 ? " done" : ""}${r.kind === "task" && !pct && taskStateOf(r) === "started" ? " started" : ""}`,
    style: { height: h },
    "data-testid": "sch-row",
    "data-row-id": r.id,
    "data-kind": r.kind,
    onMouseDown: pick,
    onKeyDown: onRowKey,
    // 行の中の欄・ボタンを触ったらその行を選ぶ（Delete・Alt＋↑↓ の相手）
    onFocus: () => onSelect(r.id),
  };

  if (r.kind === "memo") {
    return (
      <div {...common}>
        <div className="sch-memo">
          {grip}
          <svg className="ico" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 3h8l4 4v14H6z M14 3v4h4" />
          </svg>
          {readOnly ? <span className="ro">{r.name}</span> : <input className="sch-in memo" value={r.name} maxLength={500} placeholder={SCHEDULE_MSG.memo} aria-label="メモ" onChange={(e) => up({ name: e.target.value }, "name")} />}
          {menu}
        </div>
      </div>
    );
  }

  if (r.kind === "heading") {
    const s = headingSummary(rows, i);
    return (
      <div {...common}>
        <div className="sch-l">
          {grip}
          {/* 見出しも 開始・日数・担当を持てる（#291 ★3。手で入れた期間は薄い枠で描く） */}
          <span className="c-name hd">
            <button
              type="button"
              className="sch-fold"
              disabled={readOnly}
              aria-expanded={!r.collapsed}
              aria-label={r.collapsed ? `「${rowName(r)}」を展開` : `「${rowName(r)}」を折りたたみ`}
              onClick={() => ops.update(r.id, { collapsed: !r.collapsed }, { save: "now" })}
              data-testid="sch-fold"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d={r.collapsed ? "M9 6l6 6-6 6" : "M6 9l6 6 6-6"} />
              </svg>
            </button>
            <span className="sch-two">
              {readOnly ? <b className="ro" title={r.name}>{r.name}</b> : <input className="sch-in hd" value={r.name} title={r.name} maxLength={200} aria-label="見出し" onChange={(e) => up({ name: e.target.value }, "name")} />}
              {/* 2行目（#296、docs/102 §2.4.1）：説明を名前と同じ左端から列の幅いっぱいに。担当は右端（入れたときだけ。空の欄は 行を指す・選ぶ・欄に入ったときだけ） */}
              <span className="sch-asgline hd">
                {readOnly ? <span className="ro sub">{r.note}</span> : <input className="sch-in sub" value={r.note ?? ""} maxLength={200} placeholder="説明" aria-label="見出しの説明" onChange={(e) => up({ note: e.target.value }, "note")} />}
                {r.assignee || !readOnly ? (
                  <AssigneeInput value={r.assignee} cands={cands} disabled={readOnly} label="担当" onRegister={ops.registerVendor} onChange={(a: ScheduleAssignee | null) => ops.update(r.id, { assignee: a }, { save: "now" })} />
                ) : null}
              </span>
            </span>
          </span>
          <span className="c-start">
            {readOnly ? (
              <span className="ro">{r.start ? mdOf(r.start) : ""}</span>
            ) : (
              <button type="button" className="sch-date" aria-label={`見出しの開始 ${r.start ? mdOf(r.start) : "日付未定"}`} onClick={(e) => onOpenCal(e.currentTarget)} data-testid="sch-start">
                {r.start ? mdOf(r.start) : "—"}
              </button>
            )}
          </span>
          <span className="c-days">
            <DaysInput r={r} readOnly={readOnly} ops={ops} i={i} />
          </span>
          <span className="c-pg">{!pct ? <span className="ro sch-hdone" data-testid="sch-hdone">{headingDoneText(rows, i)}</span> : s.progress == null ? "" : s.progress >= 100 ? <DoneMark /> : `${s.progress}%`}</span>
          {crew}
          {amt}
          {menu}
        </div>
        <div className="sch-t">{bar}</div>
      </div>
    );
  }

  const ms = r.kind === "milestone";
  return (
    <div {...common}>
      <div className="sch-l">
        {grip}
        <span className="c-name">
          <span className="sch-two">
            <span className="sch-nm">
              {ms ? <span className={"sch-dia" + ((r.progress ?? 0) >= 100 ? " done" : "")} aria-hidden="true" /> : null}
              {readOnly ? (
                <b className="ro" title={r.name}>{r.name}</b>
              ) : (
                <input className="sch-in nm" value={r.name} title={r.name} maxLength={200} aria-label={ms ? "節目の名前" : t("{task}名")} onChange={(e) => up({ name: e.target.value }, "name")} />
              )}
            </span>
            <span className="sch-asgline">
              <AssigneeInput value={r.assignee} cands={cands} disabled={readOnly} label="担当" onRegister={ops.registerVendor} onChange={(a: ScheduleAssignee | null) => ops.update(r.id, { assignee: a }, { save: "now" })} />
              {/* 休みの日も作業する工程の印（色だけで伝えない。9/30）。工程名が切れないよう2行目の右に（#101 決めごと16） */}
              {r.kind === "task" && r.workOffDays ? (
                <span className="sch-offwork" title="休日も作業" data-testid="sch-offwork">
                  休日も
                </span>
              ) : null}
              {/* 随時（#291）。「休日も」と同じ場所 */}
              {r.kind === "task" && r.intermittent ? (
                <span className="sch-offwork" title={SCHEDULE_MSG.intermittentOn} data-testid="sch-intermittent">
                  {SCHEDULE_MSG.intermittent}
                </span>
              ) : null}
              {/* 人数は 2行目に出さない（#297。{task}に1つの人数は日で人数が動く現場に合わない＝出すのは「人」の列だけ） */}
              {/* 雨でも作業する工程の印（#105。中止のダイアログで最初から外れる）。営業力は出さない（#142 段5 No.127） */}
              {r.kind === "task" && r.rainOk && showsRain() ? (
                <span className="sch-offwork sch-rainok" title={SCHEDULE_MSG.rainOkOn} data-testid="sch-rainok">
                  {SCHEDULE_MSG.rainOk}
                </span>
              ) : null}
              {/* 重なり（#102）：同じ担当がほかの現場にも入っている。押すと相手と「から始める」 */}
              {conflict ? (
                <button
                  type="button"
                  className="sch-ovl"
                  aria-label={`${SCHEDULE_MSG.overlap}：${SCHEDULE_MSG.conflictBody(conflict.days.map(mdOf).join("・"))}`}
                  onClick={(e) => ops.conflict?.(r.id, e.currentTarget)}
                  data-testid="sch-overlap"
                >
                  {SCHEDULE_MSG.overlap}
                </button>
              ) : null}
              {/* 協力会社からの報告（#103 §6.3.4）：確かめ待ちがあれば「報告」、反映した写真があれば「写真 n」 */}
              {reported ? (
                <button type="button" className="sch-repmark" onClick={(e) => ops.report?.(r.id, e.currentTarget)} data-testid="sch-row-report">
                  {PARTNER_LINK_MSG.rowReport}
                </button>
              ) : null}
              {photoCount ? (
                <button type="button" className="sch-repmark ph" onClick={() => ops.photos?.(r.id)} data-testid="sch-row-photos">
                  {PARTNER_LINK_MSG.rowPhotos(photoCount)}
                </button>
              ) : null}
            </span>
          </span>
        </span>
        <span className="c-start">
          {readOnly ? (
            <span className="ro">{r.start ? mdOf(r.start) : "—"}</span>
          ) : (
            <button type="button" className="sch-date" aria-label={`開始 ${r.start ? mdOf(r.start) : "日付未定"}`} onClick={(e) => onOpenCal(e.currentTarget)} data-testid="sch-start">
              {r.start ? mdOf(r.start) : "—"}
            </button>
          )}
        </span>
        <span className="c-days">{ms ? <span className="ro faint">—</span> : <DaysInput r={r} readOnly={readOnly} ops={ops} i={i} />}</span>
        <span className="c-pg">
          {ms ? (
            readOnly ? (
              (r.progress ?? 0) >= 100 ? <DoneMark /> : null
            ) : (
              <button type="button" className={"sch-msdone" + ((r.progress ?? 0) >= 100 ? " on" : "")} aria-pressed={(r.progress ?? 0) >= 100} onClick={() => ops.update(r.id, { progress: (r.progress ?? 0) >= 100 ? 0 : 100 }, { save: "now" })} data-testid="sch-msdone">
                {(r.progress ?? 0) >= 100 ? <DoneMark /> : "済"}
              </button>
            )
          ) : pct ? (
            <ProgressCell r={r} readOnly={readOnly} ops={ops} late={delay > 0} />
          ) : (
            <StateCell r={r} readOnly={readOnly} ops={ops} late={delay > 0} />
          )}
        </span>
        {crew}
        {amt}
        {menu}
      </div>
      <div className="sch-t">{bar}</div>
    </div>
  );
}

function DoneMark({ check = false }: { check?: boolean }) {
  // check＝状況の「完了」（#292。％ OFF の会社：✓ だけ。字の「✓」は読み上げと紙の印にそろえる）
  if (check)
    return (
      <span className="sch-done" aria-label={TASK_STATE_LABEL.done}>
        ✓
      </span>
    );
  return (
    <span className="sch-done">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M20 6L9 17l-5-5" />
      </svg>
      済
    </span>
  );
}

/** 日数の欄：打ち終えたら（Enter・欄を出る）決める。全角も半角に。空にすると日付未定 */
function DaysInput({ r, readOnly, ops, i }: { r: ScheduleRow; readOnly: boolean; ops: GanttOps; i: number }) {
  const [text, setText] = useState(r.days == null ? "" : String(r.days));
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setText(r.days == null ? "" : String(r.days));
  }, [r.days]);
  if (readOnly) return <span className="ro num">{r.days ?? "—"}</span>;
  const commit = () => {
    const t = toHalf(text, "int").replace(/[,-]/g, "");
    if (!t) {
      if (r.days != null) ops.update(r.id, { start: null, days: null }, { save: "now" });
      return;
    }
    const n = Math.max(1, Math.min(999, Number(t)));
    setText(String(n));
    if (n !== r.days) {
      ops.update(r.id, r.start ? { days: n } : { days: n, start: ops.defaultStart(i) }, { save: "now" });
      ops.reveal(r.id);
    }
  };
  return (
    <input
      className="sch-in num r"
      type="text"
      inputMode="numeric"
      value={text}
      aria-label="日数"
      onFocus={() => (focused.current = true)}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        focused.current = false;
        commit();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !(e.nativeEvent.isComposing || e.keyCode === 229)) {
          e.preventDefault();
          commit();
        }
      }}
      data-testid="sch-days"
    />
  );
}

/** 人数の欄（#102）：1〜99。空にすると人数なし。打ち終えたら（Enter・欄を出る）決める。全角も半角に */
function CrewInput({ r, readOnly, ops }: { r: ScheduleRow; readOnly: boolean; ops: GanttOps }) {
  const [text, setText] = useState(r.crew == null ? "" : String(r.crew));
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setText(r.crew == null ? "" : String(r.crew));
  }, [r.crew]);
  if (readOnly) return <span className="ro num">{r.crew ?? ""}</span>;
  const commit = () => {
    const t = toHalf(text, "int").replace(/[,-]/g, "");
    const n = t ? Math.max(1, Math.min(99, Number(t))) : null;
    setText(n == null ? "" : String(n));
    if (n !== (r.crew ?? null)) ops.update(r.id, { crew: n }, { save: "now" });
  };
  return (
    <input
      className="sch-in num r"
      type="text"
      inputMode="numeric"
      value={text}
      aria-label={SCHEDULE_MSG.crewLabel}
      onFocus={() => (focused.current = true)}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        focused.current = false;
        commit();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !(e.nativeEvent.isComposing || e.keyCode === 229)) {
          e.preventDefault();
          commit();
        }
      }}
      data-testid="sch-crew"
    />
  );
}

/** 進みの欄：ふだんは「✓ 済」「25%」、押すと数字の欄（0〜100）。遅れていれば朱の太字 */
function ProgressCell({ r, readOnly, ops, late }: { r: ScheduleRow; readOnly: boolean; ops: GanttOps; late: boolean }) {
  const [edit, setEdit] = useState(false);
  const [text, setText] = useState("");
  const p = r.progress ?? 0;
  if (readOnly || !edit) {
    const shown = p >= 100 ? <DoneMark /> : `${p}%`;
    return readOnly ? (
      <span className={"ro" + (late ? " late" : "")}>{shown}</span>
    ) : (
      <button
        type="button"
        className={"sch-pgbtn" + (late ? " late" : "")}
        aria-label={`進捗 ${p}%`}
        onClick={() => {
          setText(String(p));
          setEdit(true);
        }}
        data-testid="sch-progress"
      >
        {shown}
      </button>
    );
  }
  const commit = () => {
    setEdit(false);
    const t = toHalf(text, "int").replace(/[,-]/g, "");
    const n = t ? Math.max(0, Math.min(100, Number(t))) : 0;
    if (n !== p) ops.update(r.id, { progress: n }, { save: "now" });
  };
  return (
    <span className="sch-pgin">
      <input
        className="sch-in num r"
        type="text"
        inputMode="numeric"
        autoFocus
        value={text}
        aria-label="進捗（%）"
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !(e.nativeEvent.isComposing || e.keyCode === 229)) {
            e.preventDefault();
            commit();
          } else if (e.key === "Escape") setEdit(false);
        }}
      />
      %
    </span>
  );
}

/**
 * 状況の欄（#292。％ OFF の会社）：未着手＝空・着手＝「着手」・完了＝✓。押すと 未着手 → 着手 → 完了 → 未着手（Schedule.tsx がトーストと元に戻す）。
 * 遅れていれば朱の太字（色だけで伝えない＝知らせの帯と「n日遅れ」の印もある）
 */
function StateCell({ r, readOnly, ops, late }: { r: ScheduleRow; readOnly: boolean; ops: GanttOps; late: boolean }) {
  const st = taskStateOf(r);
  const shown = st === "done" ? <DoneMark check /> : st === "started" ? <span className="sch-st started">{TASK_STATE_LABEL.started}</span> : "";
  const label = `${SCHEDULE_MSG.stateColumn} ${TASK_STATE_LABEL[st]}`;
  if (readOnly) {
    return (
      <span className={"ro" + (late ? " late" : "")} aria-label={label} data-testid="sch-state" data-state={st}>
        {shown}
      </span>
    );
  }
  const to = TASK_STATE_LABEL[nextTaskState(st)];
  return (
    <button
      type="button"
      className={"sch-pgbtn sch-statebtn" + (late ? " late" : "")}
      aria-label={`${label}（${SCHEDULE_MSG.stateNext(to)}）`}
      title={SCHEDULE_MSG.stateNext(to)}
      onClick={() => ops.stateClick?.(r.id)}
      data-testid="sch-state"
      data-state={st}
    >
      {shown}
    </button>
  );
}

/* ---------------- バー ---------------- */
/* ---------------- 概略（#291、docs/102 §3.2.2）：見出しの行だけ・中の{task}を名前つきで横に並べる（見るだけ） ---------------- */
const SUM_LANE = 30;
const SUM_PAD = 8;
type SumItem = { head: ScheduleRow | null; index: number; bars: ScheduleRow[]; lanes: Map<string, number>; h: number };
/** 概略の行：見出しは中の工程・節目を段に分ける（名前の幅も重ならないように）。見出しの無い工程・節目は1行。メモは出さない */
/** 見出しの中の{task}の日のメモを1つに（#299。概略の見出しの行の点）：同じ日は文を「・」でつなぐ */
function mergedNotes(bars: readonly ScheduleRow[], notes: ReadonlyMap<string, ReadonlyMap<string, string>> | undefined): Map<string, string> | undefined {
  if (!notes?.size) return undefined;
  const out = new Map<string, string>();
  for (const b of bars) for (const [d, t] of notes.get(b.id) ?? []) out.set(d, out.has(d) ? `${out.get(d)}・${t}` : t);
  return out.size ? new Map([...out].sort((a, b) => (a[0] < b[0] ? -1 : 1))) : undefined;
}
function summaryOf(rows: readonly ScheduleRow[], ax: Axis): { items: SumItem[]; total: number } {
  const items: SumItem[] = [];
  let cur: SumItem | null = null;
  rows.forEach((r, i) => {
    if (r.kind === "memo") return;
    if (r.kind === "heading") {
      cur = { head: r, index: i, bars: [], lanes: new Map(), h: ROW_H.heading };
      items.push(cur);
      return;
    }
    if (cur) cur.bars.push(r);
    else items.push({ head: null, index: i, bars: [r], lanes: new Map([[r.id, 0]]), h: ROW_H.task });
  });
  for (const it of items) {
    if (!it.head) continue;
    const dated = it.bars.filter((b) => !!b.start);
    // 名前の幅（12px の字 ＋ 余白）と右の日付（10px。#291 §2.1.1）も日数に直して数える＝名前どうし・日付どうしが重ならない
    const { lane, count } = lanesOf(
      dated.map((b) => {
        const lenDays = b.kind === "milestone" ? 1 : dayNo(b.end ?? b.start!) - dayNo(b.start!) + 1;
        const tail = b.kind === "milestone" ? 0 : BDATE_GAP + bdateW(mdOf(b.start!)) + (hasRightDate(b.start!, b.end ?? b.start!) ? BDATE_GAP + bdateW(mdOf(b.end ?? b.start!)) : 0);
        const nameDays = Math.ceil((Math.max(textWidth(b.name, 12) + (b.kind === "milestone" ? 22 : 8), ax.xr(b.end ?? b.start!) - ax.x(b.start!) + tail)) / ax.w(b.start!));
        return { id: b.id, start: b.start!, end: addDays(b.start!, Math.max(lenDays, nameDays) - 1) };
      }),
    );
    it.lanes = lane;
    it.h = Math.max(ROW_H.heading, SUM_PAD * 2 + Math.max(1, count) * SUM_LANE);
  }
  return { items, total: items.reduce((n, it) => n + it.h, 0) };
}
function SummaryRows({
  rows,
  ax,
  showCrew,
  showAmount,
  notes,
  cal,
}: {
  rows: readonly ScheduleRow[];
  ax: Axis;
  showCrew: boolean;
  showAmount: boolean;
  /** 日のメモ（#299。行の id → 日付 → 文）：見出しの行に中の{task}の点をまとめる */
  notes?: ReadonlyMap<string, ReadonlyMap<string, string>>;
  cal?: WorkCalendar;
}) {
  const pct = useContext(PctContext);
  const { x } = ax;
  // 左の表は通常の行と同じ列の並び（見出しの列とずれない）。人数・金額の列は空
  const tail = (
    <>
      {showCrew ? <span className="c-crew" /> : null}
      {showAmount ? <span className="c-amt sch-internal" /> : null}
      <span className="c-menu" />
    </>
  );
  const { items } = summaryOf(rows, ax);
  const bar = (b: ScheduleRow, top: number) => {
    if (!b.start) return null;
    if (b.kind === "milestone") {
      return (
        <span key={b.id} className={"sch-summs" + ((b.progress ?? 0) >= 100 ? " done" : "")} style={{ left: x(b.start) + ax.w(b.start) / 2 - 7, top }} data-testid="sch-sumbar" title={`${b.name} ${mdOf(b.start)}`}>
          <span className="dia" />
          <span className="lbl">{b.name}</span>
        </span>
      );
    }
    const end = b.end ?? b.start;
    const l = x(b.start);
    const w = ax.xr(end) - l;
    const done = (b.progress ?? 0) >= 100;
    return (
      <Fragment key={b.id}>
        <span
          className={"sch-sumbar" + (done ? " done" : "") + (b.intermittent ? " intm" : "")}
          style={{ left: l, top, width: w, ["--done" as string]: `${barDonePct(b, pct)}%` }}
          data-testid="sch-sumbar"
          title={`${b.name} ${spanText(b.start, end)}`}
        >
          <span className="lbl">{b.name}</span>
        </span>
        {/* 概略は名前の上に加えて両端に日付（#291 §2.1.1。段は日付の幅も数えて分けてある） */}
        <BarDates start={b.start} end={end} l={l} r={l + w} top={top} />
      </Fragment>
    );
  };
  return (
    <>
      {items.map((it) => {
        if (!it.head) {
          const r = it.bars[0]!;
          return (
            <div key={r.id} className="sch-row k-task sum" style={{ height: it.h }} data-testid="sch-row" data-row-id={r.id} data-kind={r.kind}>
              <div className="sch-l">
                <span className="c-grip" />
                <span className="c-name">
                  <b className="ro" title={r.name}>{r.name || "（名前なし）"}</b>
                </span>
                <span className="c-start">
                  <span className="ro">{r.start ? mdOf(r.start) : ""}</span>
                </span>
                <span className="c-days">
                  <span className="ro num">{r.kind === "task" ? (r.days ?? "") : ""}</span>
                </span>
                <span className="c-pg">{r.kind !== "task" ? null : !pct ? (stateMarkOf(r) === "✓" ? <DoneMark check /> : stateMarkOf(r)) : (r.progress ?? 0) >= 100 ? <DoneMark /> : `${r.progress ?? 0}%`}</span>
                {tail}
              </div>
              <div className="sch-t">
                {bar(r, 14)}
                {cal && r.kind === "task" ? <DayNoteMarks notes={notes?.get(r.id)} ax={ax} cal={cal} coarse rowId={r.id} readOnly /> : null}
              </div>
            </div>
          );
        }
        const h = it.head;
        const s = headingSummary(rows, it.index);
        return (
          <div key={h.id} className="sch-row k-heading sum" style={{ height: it.h }} data-testid="sch-row" data-row-id={h.id} data-kind="heading">
            <div className="sch-l">
              <span className="c-grip" />
              <span className="c-name hd">
                <span className="sch-two">
                  <b className="ro" title={h.name}>{h.name || "（名前なし）"}</b>
                  {h.note ? <span className="ro sub">{h.note}</span> : null}
                </span>
              </span>
              <span className="c-start">
                <span className="ro">{s.start ? mdOf(s.start) : ""}</span>
              </span>
              <span className="c-days">
                <span className="ro num">{h.days ?? ""}</span>
              </span>
              <span className="c-pg">{!pct ? headingDoneText(rows, it.index) : s.progress == null ? "" : s.progress >= 100 ? <DoneMark /> : `${s.progress}%`}</span>
              {tail}
            </div>
            <div className="sch-t">
              {s.plan ? (
                <span className="sch-plan sum" style={{ left: x(s.plan.start), width: ax.xr(s.plan.end) - x(s.plan.start), top: 6, height: it.h - 12 }} data-testid="sch-plan" aria-hidden="true" />
              ) : null}
              {it.bars.map((b) => bar(b, SUM_PAD + (it.lanes.get(b.id) ?? 0) * SUM_LANE + 12))}
              {/* 中の{task}の日のメモ（#299）：見出しの行の下の端に朱の点をまとめる（同じ日は1つ・文は「・」でつなぐ） */}
              {cal ? <DayNoteMarks notes={mergedNotes(it.bars, notes)} ax={ax} cal={cal} coarse rowId={null} readOnly /> : null}
            </div>
          </div>
        );
      })}
    </>
  );
}

function Bar({
  r,
  i,
  rows,
  ax,
  cal,
  coarse = false,
  dates = false,
  readOnly,
  sel,
  dayHits = false,
  onDown,
  onKey,
  onFocus,
  delay,
}: {
  r: ScheduleRow;
  i: number;
  rows: ScheduleRow[];
  ax: Axis;
  cal: WorkCalendar;
  /** 旬・月の表示：バーを休みで区切らず1本（#290） */
  coarse?: boolean;
  /** 選んだ行のバーの日を押せる（#293。日の表示・編集のときだけ）。押す日は data-day、押すと 休み ⇄ 作業（親のドラッグの処理が見る） */
  dayHits?: boolean;
  /** バーの両端の日付（#291 §2.1.1：旬・月はいつも・日は選んだ行か「常に表示」） */
  dates?: boolean;
  readOnly: boolean;
  sel: boolean;
  onDown: (e: RPointerEvent, t: "move" | "resl" | "resr" | "connect") => void;
  onKey: (e: RKeyboardEvent) => void;
  onFocus: () => void;
  delay: number;
}) {
  const pct = useContext(PctContext);
  const { x } = ax;
  if (r.kind === "heading") {
    const s = headingSummary(rows, i);
    if (!s.start || !s.end) return r.collapsed && s.count ? <span className="sch-count chip" style={{ left: 8 }}>{s.count}行</span> : null;
    const l = x(s.start);
    const pl = s.plan ? x(s.plan.start) : 0;
    // 手で入れた期間（#291 ★3）：薄い枠（中に工程が無ければ枠だけ）。中の工程の範囲（太い線）とずれていれば計画と実際の差が見える
    const planW = s.plan ? ax.xr(s.plan.end) - pl : 0;
    const right = Math.max(ax.xr(s.end), s.plan ? pl + planW : 0);
    return (
      <>
        {s.plan ? (
          <span className="sch-plan" style={{ left: pl, width: planW }} data-testid="sch-plan" title={SCHEDULE_MSG.headingPlanTitle(spanText(s.plan.start, s.plan.end))} aria-hidden="true" />
        ) : null}
        {s.inner ? <span className="sch-sum" style={{ left: l, width: ax.xr(s.end) - l }} data-testid="sch-sum" aria-hidden="true" /> : null}
        {/* 旬・月の表示（#291 の残り、#293）：見出しの期間の両端の日付＝中に{task}があれば中の範囲、無ければ手で入れた期間の枠 */}
        {coarse ? <BarDates start={s.start} end={s.end} l={l} r={ax.xr(s.end)} /> : null}
        {r.collapsed ? <span className="sch-count chip" style={{ left: Math.max(right, coarse ? ax.xr(s.end) + BDATE_GAP + bdateW(mdOf(s.end)) : right) + 10 }}>{s.count}行</span> : null}
      </>
    );
  }
  if (!r.start) return null;
  // 読み上げの名前は1回目のまま（遅れはバーの右の「n日遅れ」の文字で伝える）
  const label = r.kind === "milestone" ? `${rowName(r)} ${mdOf(r.start)}` : `${rowName(r)} ${mdOf(r.start)}〜${mdOf(r.end ?? r.start)} ${r.days ?? 1}日`;
  if (r.kind === "milestone") {
    const cx = x(r.start) + ax.w(r.start) / 2;
    return (
      <div
        className={"sch-ms" + ((r.progress ?? 0) >= 100 ? " done" : "") + (sel ? " sel" : "")}
        style={{ left: cx - 10 }}
        data-bar-id={r.id}
        data-testid="sch-bar"
        role={readOnly ? "img" : "button"}
        tabIndex={readOnly ? undefined : 0}
        aria-label={label}
        onPointerDown={(e) => onDown(e, "move")}
        onContextMenu={(e) => e.preventDefault()}
        onKeyDown={onKey}
        onFocus={onFocus}
      >
        <span className="dia" />
        <span className="lbl">{r.name}</span>
        {readOnly ? null : <span className="dot" onPointerDown={(e) => onDown(e, "connect")} data-testid="sch-dot" />}
      </div>
    );
  }
  const end = r.end ?? r.start;
  const l = x(r.start);
  const w = ax.xr(end) - l;
  const runs0 = workRuns(r.start, end, calendarForRow(r, cal)); // 「休みの日も作業する」工程は休みで切れない
  const total = runs0.reduce((n, s) => n + dayNo(s.to) - dayNo(s.from) + 1, 0) || 1;
  // 済んだ所（#292：％ OFF は 完了＝全部・着手＝半分・未着手＝無し＝紙と同じ barDonePct）
  let doneLeft = (total * barDonePct(r, pct)) / 100;
  // 旬・月の表示（#290）：休みで区切らず1本。済んだ所は稼働日で数えた分を、休みを含む暦の日に直す（紙の joinRuns と同じ）
  let runs = runs0;
  if (coarse && runs0.length > 1) {
    let cal0 = 0;
    for (const u of runs0) {
      const len = dayNo(u.to) - dayNo(u.from) + 1;
      if (doneLeft <= 0) break;
      if (doneLeft < len) {
        cal0 = dayNo(u.from) - dayNo(runs0[0]!.from) + doneLeft;
        doneLeft = 0;
        break;
      }
      doneLeft -= len;
      cal0 = dayNo(u.to) - dayNo(runs0[0]!.from) + 1;
    }
    runs = [{ from: runs0[0]!.from, to: runs0[runs0.length - 1]!.to }];
    doneLeft = cal0;
  }
  const done = (r.progress ?? 0) >= 100;
  // {task}の日ごとの休み・作業（#293）：選んだ行は日ごとに押せる目印（中止・{siteShort}の休みの日は押せない）。
  //   選んでいない行も、その{task}だけの休み・作業の日は乗せると吹き出し（「1/14（木）この{task}は休み」）。旬・月の表示は描かない
  const own = coarse ? undefined : cal.rowDays?.get(r.id);
  const dayMarks: ReactNode[] = [];
  if (!coarse && r.kind === "task" && (dayHits || own?.size)) {
    for (let k = dayNo(r.start); k <= dayNo(end); k++) {
      const d = addDays(r.start, k - dayNo(r.start));
      const set = own?.get(d);
      if (!dayHits && set === undefined) continue;
      const st = rowDayState(r, d, cal);
      if (st.why === "stop" || st.why === "siteOff") continue;
      const md = mdwJaOf(d, cal.holidays);
      const tip = st.why === "set" ? (st.work ? SCHEDULE_MSG.rowDayWorkTip(md) : SCHEDULE_MSG.rowDayOffTip(md)) : md;
      const title = dayHits ? `${tip}\n${st.work ? SCHEDULE_MSG.rowDayClickOff : SCHEDULE_MSG.rowDayClickWork}` : tip;
      dayMarks.push(
        <span
          key={d}
          className={"dhit" + (dayHits ? " on" : "") + (st.why === "set" ? (st.work ? " set-work" : " set-off") : "")}
          style={{ left: x(d) - l, width: ax.w(d) }}
          title={title}
          data-day={d}
          data-testid={dayHits ? "sch-dayhit" : "sch-daymark"}
          data-work={st.work ? "1" : "0"}
          aria-hidden="true"
        />,
      );
    }
  }
  return (
    <>
    <div
      className={"sch-bar" + (done ? " done" : "") + (sel ? " sel" : "") + (delay > 0 ? " late" : "") + (r.intermittent ? " intm" : "") + (dayHits ? " dayhits" : "")}
      style={{ left: l, width: w }}
      data-bar-id={r.id}
      data-testid="sch-bar"
      role={readOnly ? "img" : "button"}
      tabIndex={readOnly ? undefined : 0}
      aria-label={label}
      onPointerDown={(e) => onDown(e, "move")}
      onContextMenu={(e) => e.preventDefault()}
      onKeyDown={onKey}
      onFocus={onFocus}
    >
      {runs.map((s, k) => {
        const n = dayNo(s.to) - dayNo(s.from) + 1;
        const f = Math.max(0, Math.min(n, doneLeft)) / n;
        doneLeft -= n;
        const prev = runs[k - 1];
        return (
          <Fragment key={s.from}>
            {prev ? <span className="gap" style={{ left: x(addDays(prev.to, 1)) - l, width: x(s.from) - x(addDays(prev.to, 1)) }} /> : null}
            <span className="bseg" style={{ left: x(s.from) - l, width: ax.xr(s.to) - x(s.from), ["--done" as string]: `${Math.round(f * 100)}%` }} data-testid="sch-seg" />
          </Fragment>
        );
      })}
      {dayMarks}
      {/* 旬・月の表示ではバーの上に{task}名（#291。日の表示は左の表に名前がある）。入らなければ「…」＋ title */}
      {coarse && r.name ? (
        <span className="blbl" title={r.name} aria-hidden="true">
          {r.name}
        </span>
      ) : null}
      {done && w >= 20 ? (
        <svg className="ck" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M20 6L9 17l-5-5" />
        </svg>
      ) : null}
      {readOnly ? null : (
        <>
          <span className="h l" onPointerDown={(e) => onDown(e, "resl")} data-testid="sch-handle-l" />
          <span className="h r" onPointerDown={(e) => onDown(e, "resr")} data-testid="sch-handle-r" />
          <span className="dot" onPointerDown={(e) => onDown(e, "connect")} data-testid="sch-dot" />
        </>
      )}
    </div>
    {/* バーの両端の日付（#291 §2.1.1）。右の日付はつなぐ点（直せるとき）の外。遅れの印はその右へ */}
    {dates ? <BarDates start={r.start} end={end} l={l} r={l + w + (readOnly ? 0 : 14)} /> : null}
    {delay > 0 ? (
      <span className="sch-late" style={{ left: l + w + (readOnly ? 8 : 24) + (dates && hasRightDate(r.start!, end) ? BDATE_GAP + bdateW(mdOf(end)) : 0) }} data-testid="sch-late">
        {SCHEDULE_MSG.delayBadge(delay)}
      </span>
    ) : null}
    </>
  );
}

/* ---------------- 中止を取り消す（見出しの ☂ を押したとき） ---------------- */
function StopPop({ stop, names, anchor, onClose, onUndo }: { stop: ScheduleStop; names: string[]; anchor: HTMLElement; onClose: () => void; onUndo: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  useLayoutEffect(() => {
    const r = anchor.getBoundingClientRect();
    const w = ref.current?.offsetWidth ?? 220;
    setPos({ top: r.bottom + 6, left: Math.max(8, Math.min(r.left + r.width / 2 - w / 2, window.innerWidth - w - 8)) });
  }, [anchor]);
  useEffect(() => {
    ref.current?.querySelector<HTMLButtonElement>("button")?.focus();
    // 開いてから 250ms の scroll では閉じない（押す直前に見出しを画面に入れる送りの知らせは押したあとに届く。RowMenu と同じ）
    const openedAt = performance.now();
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node) && !anchor.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        anchor.focus();
      }
    };
    const onScroll = () => {
      if (performance.now() - openedAt < 250) return;
      onClose();
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [anchor, onClose]);
  return createPortal(
    <div ref={ref} className="sch-stoppop" role="dialog" aria-label={`${mdOf(stop.date)} の中止`} style={pos ?? { top: -9999, left: -9999 }} data-testid="sch-stoppop">
      <span>
        {mdOf(stop.date)}　{stop.reason === "rain" && showsRain() ? `☂ ${SCHEDULE_MSG.stopButton}` : stopOtherText(stop.note)}
      </span>
      <small className="muted">{stop.scope === "rows" ? names.join("・") : SCHEDULE_MSG.stopAllRows}</small>
      <button type="button" className="btn tonal" onClick={onUndo} data-testid="sch-stoppop-undo">
        {SCHEDULE_MSG.stopUndo}
      </button>
    </div>,
    document.body,
  );
}

/*
 * 工程を選んだ中止：止めた工程の行のその日だけ地色と小さな ☂（#105）。
 * 工程の最初の日を止めるとバーは次の稼働日から始まり、止めた日はバーの前になる。そこにも出す（どの工程を止めたか分かるように）
 */
function RowStops({ r, cal, ax, px, stopOf }: { r: ScheduleRow; cal: WorkCalendar; ax: Axis; px: number; stopOf: ReadonlyMap<string, ScheduleStop> }) {
  const own = cal.rowStops?.get(r.id);
  if (!own?.size) return null;
  const days = [...own].sort();
  return (
    <>
      {days.map((d) => (
        <span key={d} className="sch-rowstop" style={{ left: ax.x(d), width: ax.w(d) }} data-testid="sch-rowstop" data-date={d} aria-hidden="true">
          {px >= 20 ? (stopOf.get(d)?.reason === "other" || !showsRain() ? "止" : "☂") : null}
        </span>
      ))}
    </>
  );
}
